import { Color } from '@persona-id/design-tokens';
import { useDrop } from 'react-dnd';
import styled from 'styled-components';

import {
  WORKFLOW_STEP_CARD_HEIGHT,
  WORKFLOW_STEP_CARD_WIDTH,
} from 'dashboard/apps/InquiryTemplateEditor/WorkflowTree/WorkflowStepCard';
import {
  DraggedItem,
  KEY_DRAG_ITEM,
} from 'dashboard/apps/InquiryTemplateEditor/WorkflowTree/WorkflowTreeDragAndDropUtils';
import { StepFlowVerticalLine } from 'dashboard/components/StepFlow/StepFlowElements';
import { WorkflowStepAll } from 'dashboard/models/workflow-version';
import { BASE } from 'design-system/variables/spacing';
import useToggle from 'lib/hooks/useToggle';

const ADD_NODE_CONNECTOR_HEIGHT = 4 * BASE;

const PlusSign = () => {
  return (
    <svg width='8' height='8' viewBox='0 0 8 8' fill='none' xmlns='http://www.w3.org/2000/svg'>
      <path
        d='M4 1.66602V6.33268'
        stroke='white'
        strokeWidth='1.5'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
      <path
        d='M1.66666 4H6.33332'
        stroke='white'
        strokeWidth='1.5'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  );
};

const AddCircle = styled('div')<{ isHovered: boolean; isLast?: boolean }>`
  ${({ isHovered, isLast }) => {
    const diameter = isHovered || isLast ? 14 : 10;

    return `
      position: absolute;
      border-radius: 50%;
      display: flex;
      justify-content: center;
      align-items: center;
      width: ${diameter}px;
      height: ${diameter}px;
      top: ${(ADD_NODE_CONNECTOR_HEIGHT - diameter) / 2}px;
      left: ${(WORKFLOW_STEP_CARD_WIDTH - diameter) / 2}px;
      background-color: ${isHovered || isLast ? Color.CorePurple800 : Color.PrimaryWhite};
      border: ${
        isHovered || isLast
          ? `2px solid ${Color.CorePurple800}`
          : `2px solid ${Color.CorePurple200}`
      };
      transition: background-color,border .25s;
      cursor: pointer;
      ${isLast && 'bottom: 0;'}
    `;
  }}
`;

const Container = styled('div')<{ active: boolean; expanded: boolean }>`
  position: relative;
  min-height: ${({ expanded }) =>
    expanded ? WORKFLOW_STEP_CARD_HEIGHT * 1.25 : ADD_NODE_CONNECTOR_HEIGHT}px;
  width: ${WORKFLOW_STEP_CARD_WIDTH}px;
  display: flex;
  flex-direction: column;
  align-items: center;
  ${({ active }) => active && 'cursor: pointer;'}
  transition: min-height .25s;
  ${({ expanded, theme }) => expanded && `border-radius: ${theme.card.borderRadius};`}
`;

const FakeRectangle = styled('div')<{ active: boolean }>`
  position: absolute;
  width: inherit;
  height: calc(100% - 8px);
  top: 4px;
  ${({ active, theme }) =>
    active &&
    `
    background-color: ${Color.CorePurple200};
    border-radius: ${theme.card.borderRadius};
    transition: background-color .25s;
    cursor: pointer;
  `}
`;

interface WorkflowTreeAddStepButtonProps {
  aroundNodes: WorkflowStepAll[];
  editable: boolean;
  handleAddNode: () => void;
  isLast?: boolean;
  onDrop: (droppedNodeToken: string) => void;
  lineColor?: string;
}

const WorkflowTreeAddStepButton = ({
  aroundNodes,
  editable,
  handleAddNode,
  isLast,
  onDrop,
  lineColor,
}: WorkflowTreeAddStepButtonProps) => {
  const [isActive, { enable: activate, disable: deactivate }] = useToggle(false);

  const [{ isOver, canDrop, isDragging }, drop] = useDrop<
    DraggedItem,
    unknown,
    { isOver: boolean; canDrop: boolean; isDragging: boolean }
  >(
    () => ({
      accept: KEY_DRAG_ITEM,
      collect: (monitor) => ({
        canDrop: monitor.canDrop(),
        isOver: monitor.isOver(),
        isDragging: !!monitor.getItem(),
      }),
      canDrop: (item) => {
        const aroundNodeTokens = aroundNodes.map((node) => node.token);
        return !aroundNodeTokens.includes(item.token);
      },
      drop: (item) => onDrop(item.token),
    }),
    [onDrop],
  );

  const addNode = () => {
    if (editable && isActive) {
      deactivate();
      handleAddNode();
    }
  };

  const canDropAndOver = canDrop && isOver;

  return (
    <Container
      id={`workflow-tree__add-step-button-${aroundNodes[0].token}`}
      aria-label={`Add step after ${aroundNodes[0].token}`}
      data-test={`workflow-tree__add-step-button-${aroundNodes[0].token}`}
      active={isActive || canDropAndOver}
      expanded={canDropAndOver}
      // While dragging a step card, this listener is called unintentionally somehow
      onMouseEnter={() => !isDragging && activate()}
      onMouseLeave={deactivate}
      onClick={addNode}
      ref={drop}
    >
      <StepFlowVerticalLine width={WORKFLOW_STEP_CARD_WIDTH} color={lineColor} />
      {editable && (
        <>
          <FakeRectangle active={isActive || canDropAndOver} />
          {!isOver && (
            <AddCircle
              isHovered={isActive || canDrop}
              isLast={isLast}
              data-test={`workflow-tree__add-step-button-${aroundNodes[0].token}__circle`}
            >
              <PlusSign />
            </AddCircle>
          )}
        </>
      )}
    </Container>
  );
};

export default WorkflowTreeAddStepButton;
